import { Autotractor } from "protos/atk/autotractor";
import {
  ConfigValidationResult,
  SearchRigsResponse,
} from "protos/portal/atk_equipment";
import { convert } from "common/utils/units/units";
import { CreateJobRequest } from "protos/rtc/jobs";
import { Direction } from "protos/rtc/path_plan";
import { FarmMapData, getPlantingHeading } from "common/utils/farm";
import { JobType } from "protos/atk/job_type";
import { jobTypeToLocaleString } from "common/utils/enumTranslations";
import { normalizeAzimuth } from "common/utils/geo";
import { TFunction } from "i18next";

export enum WizardStep {
  JOB_TYPE = 0,
  SELECT_FIELDS = 1,
  CONFIGURE_PATH_AND_PREVIEW = 2,
  CONFIRM = 3,
  SELECT_PATH = 4,
}

export interface NewJobsFormState {
  activeStep: WizardStep;
  jobType: JobType | undefined;
  seeAllCustomers: boolean;
  showFieldsOnFarmId: string;
  fieldSearch: string;
  selectedFieldIds: string[];
  laserWeed: {
    rowSpacingM: number;
  };
  groundPrep: {
    rowHeadingMode: "north" | "planting";
    rowHeadingOffset: number;
    workingOverlapM: number;
    turnDirection: Direction;
    headlandWidthM: number;
    workHeadlandsWhen: "none" | "before" | "after";
  };
  previewSelectedRigId: string | undefined;
  jobName: string;
}

export function stepSequence(formState: NewJobsFormState): WizardStep[] {
  switch (formState.jobType) {
    case JobType.TRANSPORT: {
      return [WizardStep.JOB_TYPE, WizardStep.SELECT_PATH, WizardStep.CONFIRM];
    }
    case JobType.IN_SEASON: {
      return [
        WizardStep.JOB_TYPE,
        WizardStep.SELECT_FIELDS,
        WizardStep.SELECT_PATH,
        WizardStep.CONFIGURE_PATH_AND_PREVIEW,
        WizardStep.CONFIRM,
      ];
    }
    default: {
      return [
        WizardStep.JOB_TYPE,
        WizardStep.SELECT_FIELDS,
        WizardStep.CONFIGURE_PATH_AND_PREVIEW,
        WizardStep.CONFIRM,
      ];
    }
  }
}

export function neighboringStep(
  formState: NewJobsFormState,
  direction: "next" | "prev"
): WizardStep | undefined {
  const seq = stepSequence(formState);
  const activeStepIndex = seq.indexOf(formState.activeStep);

  if (activeStepIndex === -1) {
    throw new Error("neighboringStep: activeStep not in step sequence?");
  }

  if (direction === "next") {
    return seq[activeStepIndex + 1];
  } else {
    return seq[activeStepIndex - 1];
  }
}

export function initialValues(seeAllCustomers: boolean): NewJobsFormState {
  return {
    activeStep: WizardStep.JOB_TYPE,
    jobType: undefined,
    seeAllCustomers,
    showFieldsOnFarmId: "",
    fieldSearch: "",
    selectedFieldIds: [],
    groundPrep: {
      rowHeadingMode: "north",
      rowHeadingOffset: 0,
      workingOverlapM: 0.5,
      turnDirection: Direction.CW,
      headlandWidthM: 10,
      workHeadlandsWhen: "none",
    },
    laserWeed: {
      rowSpacingM: convert(20).from("ft").to("m"),
    },
    previewSelectedRigId: undefined,
    jobName: "",
  };
}

/**
 * the planting path an in-season job follows is stored in
 * the `selectedFieldIds` array along with the field boundary.
 */
export function inSeasonPathId(
  formState: NewJobsFormState,
  farmMapData: { [farmId: string]: FarmMapData },
  farmIdByFieldId: { [fieldId: string]: string }
): string | undefined {
  return formState.selectedFieldIds.find((zoneId) => {
    const farm = farmMapData[farmIdByFieldId[zoneId] ?? ""];
    return farm?.zonesById[zoneId]?.contents?.path !== undefined;
  });
}

/**
 * the zones jobs are created for.  for in-season jobs the planting
 * path is a job parameter rather than a zone to work, so it's excluded.
 */
export function jobFieldIds(
  formState: NewJobsFormState,
  farmMapData: { [farmId: string]: FarmMapData },
  farmIdByFieldId: { [fieldId: string]: string }
): string[] {
  if (formState.jobType !== JobType.IN_SEASON) {
    return formState.selectedFieldIds;
  }

  const pathId = inSeasonPathId(formState, farmMapData, farmIdByFieldId);

  return formState.selectedFieldIds.filter((zoneId) => zoneId !== pathId);
}

export function formStateToCreateJobRequest(
  farmMapData: FarmMapData,
  nameGenerator: (fieldName: string) => string,
  customerId: string | undefined,
  fieldId: string,
  dryRun: boolean,
  formState: NewJobsFormState,
  options: {
    rigIds?: string[];
  }
): CreateJobRequest[] {
  const field = farmMapData.zonesById[fieldId];
  if (!field) {
    throw new Error("formStateToCreateJobRequest: no field");
  }

  const fieldName = field.name;

  if (!customerId) {
    throw new Error("formStateToCreateJobRequest: no customer ID");
  }

  switch (formState.jobType) {
    case JobType.GROUND_PREP: {
      let rowHeading: number | undefined;

      if (formState.groundPrep.rowHeadingMode === "north") {
        rowHeading = formState.groundPrep.rowHeadingOffset;
      } else {
        rowHeading = getPlantingHeading(field, farmMapData.pointsById);

        if (rowHeading === undefined) {
          throw new Error(
            "formStateToCreateJobRequest: couldn't determine row heading"
          );
        }

        rowHeading = normalizeAzimuth(
          rowHeading + formState.groundPrep.rowHeadingOffset
        );
      }

      return (options.rigIds ?? []).map((rigId) => ({
        type: JobType.GROUND_PREP,
        name: nameGenerator(fieldName),
        farmId: farmMapData.id?.id ?? "",
        fieldId,
        customerId,
        priority: 1,
        generateObjectives: true,
        dryRun,
        groundPrep: {
          pathPlan: {
            doHeadlandFirst:
              formState.groundPrep.workHeadlandsWhen === "before",
            rowHeadingDeg: rowHeading,
            headlandWidthM: formState.groundPrep.headlandWidthM,
            // HACK: for "non-legacy" headlands, this value needs to have a zero
            // value and a value that activates the new headland code's
            // auto-compute behavior.  this came in very hot in M5 and for time
            // reasons, this is activated by the magic number 273 pending a proto
            // change. (ColinB, 2/5/26)
            numHeadlandPasses:
              formState.groundPrep.workHeadlandsWhen === "none" ? 0 : 273,
            splitOnCenterPivots: true,
            flipRowOrder: false,
            workingOverlapM: formState.groundPrep.workingOverlapM,
            turnDirection: formState.groundPrep.turnDirection,
            activities: undefined,
            rigActivities: undefined,
            toolSpeedMS: 0.1,
            transitionSpeedMS: 0.1,
          },
          rigId,
          // parameters only written by the jobs service, never read
          rigSnapshotId: "",
          updatedAt: undefined,
        },
      }));
    }
    case JobType.LASER_WEED: {
      return [
        {
          type: JobType.LASER_WEED,
          name: nameGenerator(fieldName),
          farmId: farmMapData.id?.id ?? "",
          fieldId,
          customerId,
          priority: 1,
          generateObjectives: true,
          dryRun,
          laserWeed: {
            rowSpacingM: formState.laserWeed.rowSpacingM,
            // parameters only written by the jobs service, never read
            updatedAt: undefined,
          },
        },
      ];
    }
    case JobType.IN_SEASON: {
      const pathId = formState.selectedFieldIds.find(
        (zoneId) => farmMapData.zonesById[zoneId]?.contents?.path !== undefined
      );

      if (!pathId) {
        throw new Error("formStateToCreateJobRequest: no planting path");
      }

      return [
        {
          type: JobType.IN_SEASON,
          name: nameGenerator(fieldName),
          farmId: farmMapData.id?.id ?? "",
          fieldId,
          customerId,
          priority: 1,
          generateObjectives: true,
          dryRun,
          inSeason: {
            pathId,
            // parameters only written by the jobs service, never read
            updatedAt: undefined,
          },
        },
      ];
    }
    case JobType.TRANSPORT: {
      return [
        {
          type: JobType.TRANSPORT,
          name: nameGenerator(fieldName),
          farmId: farmMapData.id?.id ?? "",
          fieldId,
          customerId,
          priority: 1,
          generateObjectives: true,
          dryRun,
          transport: {
            pathId: fieldId,
            transportSpeedMS: convert(5).from("mph").to("m/s"),
            // parameters only written by the jobs service, never read
            updatedAt: undefined,
          },
        },
      ];
    }
    default: {
      throw new Error(
        `formStateToCreateJobRequest: unexpected job type ${formState.jobType}`
      );
    }
  }
}

/**
 * identifies all "valid" rigs for this job creation.  "valid" rigs have all
 * of these qualities:
 * - they are ready for autonomy.
 * - the job type of the rig matches the type of this job.
 * @param searchRigsResponse the response to a `/v1/atk-equipment/rigs/search`
 *   request that was issued with `{validate: true}`
 */
export function validRigs(
  searchRigsResponse: SearchRigsResponse,
  values: NewJobsFormState
): Autotractor[] {
  return searchRigsResponse.rigs.filter((rig) => {
    const validationResults = searchRigsResponse.validationResults[rig.id];
    const rigMatchesType = rig.jobSettings?.jobType === values.jobType;

    return (
      (!validationResults || validationResults.errors.length === 0) &&
      rigMatchesType
    );
  });
}

/**
 * returns why a rig is invalid for the current job, or undefined if valid.
 */
export function rigInvalidReason(
  rig: Autotractor,
  jobType: JobType | undefined,
  validationResult: ConfigValidationResult | undefined,
  t: TFunction
): string | undefined {
  const reasons: string[] = [];

  if (jobType && rig.jobSettings?.jobType !== jobType) {
    reasons.push(
      t("views.autotractor.jobCreation.step3ConfigurePreview.wrongJobType", {
        rigJobType: jobTypeToLocaleString(
          t,
          rig.jobSettings?.jobType ?? JobType.JOB_TYPE_UNSPECIFIED
        ),
      })
    );
  }

  if (validationResult && validationResult.errors.length > 0) {
    reasons.push(
      t("views.autotractor.jobCreation.step3ConfigurePreview.configErrors", {
        count: validationResult.errors.length,
      })
    );
  }

  return reasons.length > 0 ? reasons.join(". ") : undefined;
}

export function makeJobName(
  values: NewJobsFormState,
  fieldName: string
): string {
  return `${values.jobName} - ${fieldName}`;
}
